import type { ErrorKind } from '../api/client'

const STORAGE_KEY = 'gv_debug_log'
const FLAG_KEY = 'gv_debug'
const MAX_ENTRIES = 50

export interface LogEntry {
  ts: string       // ISO timestamp
  kind: ErrorKind
  status: number
  message: string
  url?: string     // tile URL when available
  stack?: string   // JS crash stack (crashGuard only); absent for proxy errors
}

// ---- Store ----------------------------------------------------------------

const entries: LogEntry[] = (() => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as LogEntry[]) : []
  } catch {
    return []
  }
})()

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch { /* storage full — ignore */ }
}

export function addLogEntry(
  kind: ErrorKind,
  status: number,
  message: string,
  url?: string,
  stack?: string,
): void {
  entries.unshift({ ts: new Date().toISOString(), kind, status, message, url, stack })
  if (entries.length > MAX_ENTRIES) entries.length = MAX_ENTRIES
  persist()
  refreshPanel()
}

/** Non-sensitive summary for the client-facing "Copy problem details" button.
 *  Contains only category data + the 10 newest buffered errors. */
export function buildDiagnostic(code: string): string {
  return JSON.stringify(
    {
      code,
      when: new Date().toISOString(),
      viewerUrl: location.href,
      userAgent: navigator.userAgent,
      recent: entries.slice(0, 10),
    },
    null,
    2,
  )
}

export function clearLog(): void {
  entries.length = 0
  persist()
  refreshPanel()
}

export function isDebugEnabled(): boolean {
  return localStorage.getItem(FLAG_KEY) === '1'
}

// ---- Panel ----------------------------------------------------------------

let panelEl: HTMLElement | null = null
let buttonEl: HTMLElement | null = null
let unseen = 0

export function mountDebugButton(container: HTMLElement): void {
  buttonEl = document.createElement('button')
  buttonEl.className = 'btn btn--small debug-btn'
  buttonEl.addEventListener('click', togglePanel)
  container.appendChild(buttonEl)
  updateBadge()
}

function togglePanel(): void {
  if (panelEl) {
    panelEl.remove()
    panelEl = null
  } else {
    openPanel()
  }
}

function openPanel(): void {
  unseen = 0
  updateBadge()

  panelEl = document.createElement('div')
  panelEl.className = 'debug-panel'
  panelEl.innerHTML = `
    <div class="debug-panel__header">
      <strong>Debug Log</strong>
      <div class="debug-panel__actions">
        <button class="btn btn--small" id="dbg-copy">Copy JSON</button>
        <button class="btn btn--small" id="dbg-clear">Clear</button>
        <button class="btn btn--small" id="dbg-close">✕</button>
      </div>
    </div>
    <div class="debug-panel__body"></div>`

  panelEl.querySelector('#dbg-copy')!.addEventListener('click', () => {
    navigator.clipboard.writeText(JSON.stringify(entries, null, 2))
      .catch(() => {/* clipboard unavailable */})
  })
  panelEl.querySelector('#dbg-clear')!.addEventListener('click', clearLog)
  panelEl.querySelector('#dbg-close')!.addEventListener('click', togglePanel)

  document.body.appendChild(panelEl)
  renderRows()
}

function renderRows(): void {
  if (!panelEl) return
  const body = panelEl.querySelector('.debug-panel__body')!
  if (entries.length === 0) {
    body.innerHTML = '<p class="debug-panel__empty">No errors recorded.</p>'
    return
  }
  body.innerHTML = `
    <table class="debug-table">
      <thead>
        <tr><th>Time</th><th>Kind</th><th>Status</th><th>Message / URL</th></tr>
      </thead>
      <tbody>
        ${entries.map((e) => `
          <tr class="debug-row debug-row--${e.kind}">
            <td class="debug-cell--time">${e.ts.slice(11, 23)}</td>
            <td><span class="debug-badge debug-badge--${e.kind}">${e.kind}</span></td>
            <td>${e.status}</td>
            <td class="debug-cell--msg">${escRow(e.url ?? e.message)}</td>
          </tr>`).join('')}
      </tbody>
    </table>`
}

function refreshPanel(): void {
  if (panelEl) {
    renderRows()
  } else {
    unseen++
    updateBadge()
  }
}

function updateBadge(): void {
  if (!buttonEl) return
  buttonEl.textContent = unseen > 0 ? `Debug ●${unseen}` : 'Debug'
  buttonEl.classList.toggle('debug-btn--alert', unseen > 0)
}

function escRow(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
